import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity, faPlus, faEdit, faTrash, faCheckCircle, faBan,
  faUsers, faToggleOn, faToggleOff, faRotate, faSearch, faXmark, faCoins,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';
const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const AGENT_STATUS_MAP: Record<string, StatusDef> = {
  active:   { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: faCheckCircle, label: 'ใช้งาน'  },
  inactive: { bg: 'bg-gray-100 dark:bg-slate-700',      text: 'text-gray-600 dark:text-slate-400',   icon: faBan,         label: 'ปิดใช้' },
};

const bankCodeColors: Record<string, string> = {
  SCB:   'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  KBANK: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  BBL:   'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  KTB:   'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  BAY:   'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  GSB:   'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400',
};

type StatusFilter = 'all' | 'active' | 'inactive';

interface AgentBank {
  id: string;
  name: string;
  email: string;
  deposit_commission_rate: number;
  withdrawal_commission_rate: number;
  is_active: boolean;
  created_at: string;
  agent_bank_assignments: { bank_id: string; banks: Bank | null }[];
}

interface Bank {
  id: string;
  name: string;
  bank_code: string;
  account_number: string;
  is_active?: boolean;
}

interface FormState {
  name: string;
  email: string;
  password: string;
  deposit_commission_rate: string;
  withdrawal_commission_rate: string;
  bank_ids: string[];
}

const emptyForm: FormState = {
  name: '',
  email: '',
  password: '',
  deposit_commission_rate: '',
  withdrawal_commission_rate: '',
  bank_ids: [],
};

interface BankSelectorProps {
  banks: Bank[];
  bankIds: string[];
  onToggle: (id: string) => void;
  takenMap: Record<string, string>;
}

const BankSelector: React.FC<BankSelectorProps> = ({ banks, bankIds, onToggle, takenMap }) => (
  <div>
    <label className={LABEL_CLASS}>
      เลือกธนาคาร ({bankIds.length} ที่เลือก)
    </label>
    <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden max-h-60 overflow-y-auto">
      {banks.length === 0 ? (
        <p className="text-center text-gray-400 dark:text-slate-500 text-sm py-6">ไม่มีธนาคารในระบบ</p>
      ) : (
        banks.map((bank) => {
          const takenBy = takenMap[bank.id];
          const isTaken = !!takenBy;
          return (
            <label
              key={bank.id}
              className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-slate-700 last:border-0 transition-colors ${
                isTaken ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-700/30' : 'hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={bankIds.includes(bank.id)}
                onChange={() => !isTaken && onToggle(bank.id)}
                disabled={isTaken}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--theme-primary)' }}
              />
              <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${bankCodeColors[bank.bank_code] || 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>
                {bank.bank_code}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{bank.name}</p>
                <p className="text-xs text-gray-400 dark:text-slate-500">{bank.account_number}</p>
              </div>
              {isTaken ? (
                <span className="text-xs text-orange-500 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-2 py-0.5 rounded whitespace-nowrap">
                  ใช้โดย: {takenBy}
                </span>
              ) : !bank.is_active ? (
                <span className="text-xs text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded">ปิดใช้งาน</span>
              ) : null}
            </label>
          );
        })
      )}
    </div>
  </div>
);

interface FormFieldsProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  banks: Bank[];
  takenMap: Record<string, string>;
  isEdit?: boolean;
}

const FormFields: React.FC<FormFieldsProps> = ({ form, setForm, banks, takenMap, isEdit = false }) => {
  const toggleBank = (bankId: string) => {
    setForm((prev) => ({
      ...prev,
      bank_ids: prev.bank_ids.includes(bankId)
        ? prev.bank_ids.filter((id) => id !== bankId)
        : [...prev.bank_ids, bankId],
    }));
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS}>
            ชื่อ Agent <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            required
            placeholder="ชื่อ Agent"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>
            อีเมล <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            required
            placeholder="agent@example.com"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      <div>
        <label className={LABEL_CLASS}>
          รหัสผ่าน {!isEdit && <span className="text-red-400">*</span>}
          {isEdit && <span className="text-gray-400 dark:text-slate-500 normal-case font-normal ml-1">(เว้นว่างหากไม่เปลี่ยน)</span>}
        </label>
        <input
          type="password"
          value={form.password}
          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          required={!isEdit}
          placeholder={isEdit ? 'เว้นว่างหากไม่เปลี่ยน' : '••••••••'}
          className={INPUT_CLASS}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS}>
            ค่าคอมฝาก (%) <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.deposit_commission_rate}
              onChange={(e) => setForm((p) => ({ ...p, deposit_commission_rate: e.target.value }))}
              required
              placeholder="0.00"
              className={`${INPUT_CLASS} pr-8`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm">%</span>
          </div>
        </div>
        <div>
          <label className={LABEL_CLASS}>
            ค่าคอมถอน (%) <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.withdrawal_commission_rate}
              onChange={(e) => setForm((p) => ({ ...p, withdrawal_commission_rate: e.target.value }))}
              required
              placeholder="0.00"
              className={`${INPUT_CLASS} pr-8`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm">%</span>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-slate-700 pt-4">
        <BankSelector banks={banks} bankIds={form.bank_ids} onToggle={toggleBank} takenMap={takenMap} />
      </div>
    </>
  );
};

const AgentBanksPage: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentBank[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ ...emptyForm });

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || 'null');
  const isAdminUser = adminUser?.type === 'admin' || !adminUser?.type;

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [agentsRes, banksRes] = await Promise.all([
        merchantApi.get('/admin/agent-banks'),
        merchantApi.get('/admin/agent-banks/banks'),
      ]);
      if (agentsRes.data.success) setAgents(agentsRes.data.data);
      if (banksRes.data.success) setBanks(banksRes.data.data);
    } catch {
      toast.error('ดึงข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setShowCreate(true);
  };

  const openEdit = (agent: AgentBank) => {
    setForm({
      name: agent.name,
      email: agent.email,
      password: '',
      deposit_commission_rate: String(agent.deposit_commission_rate),
      withdrawal_commission_rate: String(agent.withdrawal_commission_rate),
      bank_ids: agent.agent_bank_assignments.map((a) => a.bank_id),
    });
    setEditId(agent.id);
    setShowEdit(true);
  };

  const closeCreate = () => {
    if (saving) return;
    setShowCreate(false);
  };

  const closeEdit = () => {
    if (saving) return;
    setShowEdit(false);
    setEditId(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await merchantApi.post('/admin/agent-banks', {
        name: form.name,
        email: form.email,
        password: form.password,
        deposit_commission_rate: Number(form.deposit_commission_rate),
        withdrawal_commission_rate: Number(form.withdrawal_commission_rate),
        bank_ids: form.bank_ids,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setShowCreate(false);
        fetchAll();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'สร้างไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        email: form.email,
        deposit_commission_rate: Number(form.deposit_commission_rate),
        withdrawal_commission_rate: Number(form.withdrawal_commission_rate),
        bank_ids: form.bank_ids,
      };
      if (form.password) payload.password = form.password;

      const res = await merchantApi.put(`/admin/agent-banks/${editId}`, payload);
      if (res.data.success) {
        toast.success(res.data.message);
        setShowEdit(false);
        setEditId(null);
        fetchAll();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'แก้ไขไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (agent: AgentBank) => {
    try {
      await merchantApi.put(`/admin/agent-banks/${agent.id}`, { is_active: !agent.is_active });
      toast.success(`${!agent.is_active ? 'เปิด' : 'ปิด'}การใช้งานสำเร็จ`);
      fetchAll();
    } catch {
      toast.error('เปลี่ยนสถานะไม่สำเร็จ');
    }
  };

  const handleDelete = async (agent: AgentBank) => {
    const ok = await swalConfirm({
      title: `ลบ "${agent.name}"?`,
      text: 'การลบไม่สามารถย้อนกลับได้',
      icon: 'warning',
      confirmText: 'ลบ',
      confirmColor: '#ef4444',
    });
    if (!ok) return;

    setDeleting(agent.id);
    try {
      const res = await merchantApi.delete(`/admin/agent-banks/${agent.id}`);
      if (res.data.success) {
        toast.success(res.data.message);
        fetchAll();
      }
    } catch {
      toast.error('ลบไม่สำเร็จ');
    } finally {
      setDeleting(null);
    }
  };

  const activeCount = agents.filter((a) => a.is_active).length;
  const inactiveCount = agents.length - activeCount;
  const totalBankAssignments = agents.reduce((sum, a) => sum + a.agent_bank_assignments.length, 0);

  const allTakenMap: Record<string, string> = {};
  agents.forEach((agent) => {
    agent.agent_bank_assignments.forEach((a) => {
      allTakenMap[a.bank_id] = agent.name;
    });
  });

  const takenMapForCreate = allTakenMap;
  const takenMapForEdit: Record<string, string> = { ...allTakenMap };
  if (editId) {
    const currentAgent = agents.find((a) => a.id === editId);
    currentAgent?.agent_bank_assignments.forEach((a) => {
      delete takenMapForEdit[a.bank_id];
    });
  }

  const filteredAgents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return agents.filter((a) => {
      if (statusFilter === 'active' && !a.is_active) return false;
      if (statusFilter === 'inactive' && a.is_active) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q)
      );
    });
  }, [agents, searchQuery, statusFilter]);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faUsers,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'Agent ทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100">{agents.length.toLocaleString()}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'ใช้งานอยู่',
      value: <span className="text-emerald-600 dark:text-emerald-400">{activeCount.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ปิดใช้ {inactiveCount}</span>,
    },
    {
      icon: faUniversity,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'ธนาคารที่ผูกทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100">{totalBankAssignments.toLocaleString()}</span>,
    },
  ], [agents.length, activeCount, inactiveCount, totalBankAssignments]);

  const columns: Column<AgentBank>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Agent',
      sortable: true,
      render: (agent) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--theme-primary-light)' }}
          >
            <FontAwesomeIcon icon={faUniversity} className="text-xs" style={{ color: 'var(--theme-primary)' }} />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-gray-800 dark:text-slate-100 truncate">{agent.name}</p>
            <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{agent.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'deposit_commission_rate',
      header: 'ค่าคอมฝาก',
      sortable: true,
      width: '100px',
      render: (agent) => (
        <span className="px-2 py-0.5 rounded-md text-[12px] font-semibold bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          {agent.deposit_commission_rate}%
        </span>
      ),
    },
    {
      key: 'withdrawal_commission_rate',
      header: 'ค่าคอมถอน',
      sortable: true,
      width: '100px',
      render: (agent) => (
        <span className="px-2 py-0.5 rounded-md text-[12px] font-semibold bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
          {agent.withdrawal_commission_rate}%
        </span>
      ),
    },
    {
      key: 'banks',
      header: 'ธนาคารที่ผูก',
      render: (agent) => (
        agent.agent_bank_assignments.length === 0 ? (
          <span className="text-[11px] text-gray-400 dark:text-slate-500">ยังไม่ผูกธนาคาร</span>
        ) : (
          <div className="flex flex-col gap-1.5 min-w-[160px]">
            {agent.agent_bank_assignments.map((a) => a.banks && (
              <div key={a.bank_id} className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold flex-shrink-0 ${bankCodeColors[a.banks.bank_code] || 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>
                  {a.banks.bank_code}
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-800 dark:text-slate-200 font-medium leading-none truncate">{a.banks.name}</p>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 font-mono truncate">{a.banks.account_number}</p>
                </div>
              </div>
            ))}
          </div>
        )
      ),
    },
    {
      key: 'is_active',
      header: 'สถานะ',
      sortable: true,
      width: '110px',
      render: (agent) => (
        <StatusBadge status={agent.is_active ? 'active' : 'inactive'} statusMap={AGENT_STATUS_MAP} />
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      width: '140px',
      render: (agent) => (
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/agent-commissions/${agent.id}`); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
            title="ดูค่าคอมมิชชั่น"
          >
            <FontAwesomeIcon icon={faCoins} className="text-[10px]" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleToggleStatus(agent); }}
            className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors flex items-center gap-1 ${
              agent.is_active
                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600'
            }`}
            title={agent.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
          >
            <FontAwesomeIcon icon={agent.is_active ? faToggleOn : faToggleOff} className="text-[10px]" />
            {agent.is_active ? 'เปิด' : 'ปิด'}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openEdit(agent); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            title="แก้ไข"
          >
            <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDelete(agent); }}
            disabled={deleting === agent.id}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
            title="ลบ"
          >
            <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
          </button>
        </div>
      ),
    },
  ], [deleting]);

  const handleFilterReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  if (!isAdminUser) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-slate-400">
        <FontAwesomeIcon icon={faBan} className="text-4xl text-red-300 dark:text-red-500/50 mb-3" />
        <p>ไม่มีสิทธิ์เข้าถึงหน้านี้ — เฉพาะ Admin เท่านั้น</p>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faUniversity}
        title="Agent Bank"
        subtitle={`จัดการบัญชีธนาคารของ Agent และค่าคอมมิชชั่น • ${agents.length.toLocaleString()} รายการ`}
        actions={
          <UiButton icon={faPlus} onClick={openCreate}>
            เพิ่ม Agent Bank
          </UiButton>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={3} />

      <UiFilterPanel
        onSearch={() => {}}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => fetchAll()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        <div className="flex flex-wrap items-end gap-4 w-fit">
          <div className="w-[280px] shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
              <FontAwesomeIcon icon={faSearch} className="mr-1" />
              ค้นหา
            </label>
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ค้นหาชื่อ, อีเมล..."
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                >
                  <FontAwesomeIcon icon={faXmark} className="text-sm" />
                </button>
              )}
            </div>
          </div>

          <div className="w-[160px] shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className={FILTER_SELECT_CLASS}
            >
              <option value="all">ทั้งหมด ({agents.length})</option>
              <option value="active">ใช้งาน ({activeCount})</option>
              <option value="inactive">ปิดใช้ ({inactiveCount})</option>
            </select>
          </div>
        </div>
      </UiFilterPanel>

      <DataTable
        key={`${searchQuery}|${statusFilter}`} // filter เปลี่ยน → reset หน้า pagination ภายใน
        data={filteredAgents}
        columns={columns}
        loading={loading}
        keyExtractor={(a) => a.id}
        emptyMessage={searchQuery ? 'ไม่พบ Agent ที่ค้นหา' : 'ยังไม่มี Agent Bank'}
        emptyIcon={<FontAwesomeIcon icon={faUniversity} className="text-5xl text-gray-200 dark:text-slate-600" />}
        onRowClick={(agent) => navigate(`/admin/agent-commissions/${agent.id}`)}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true,
        }}
      />

      <UiModal
        open={showCreate}
        onClose={closeCreate}
        title="เพิ่ม Agent Bank"
        icon={faUniversity}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeCreate} disabled={saving} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              loading={saving}
              onClick={() => (document.getElementById('agent-create-form') as HTMLFormElement | null)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              สร้าง Agent Bank
            </UiButton>
          </div>
        }
      >
        <form id="agent-create-form" onSubmit={handleCreate} className="space-y-4">
          <FormFields form={form} setForm={setForm} banks={banks} takenMap={takenMapForCreate} />
        </form>
      </UiModal>

      <UiModal
        open={showEdit}
        onClose={closeEdit}
        title="แก้ไข Agent Bank"
        icon={faEdit}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeEdit} disabled={saving} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              loading={saving}
              onClick={() => (document.getElementById('agent-edit-form') as HTMLFormElement | null)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              บันทึกการแก้ไข
            </UiButton>
          </div>
        }
      >
        <form id="agent-edit-form" onSubmit={handleEdit} className="space-y-4">
          <FormFields form={form} setForm={setForm} banks={banks} takenMap={takenMapForEdit} isEdit />
        </form>
      </UiModal>
    </div>
  );
};

export default AgentBanksPage;
